<template>
  <div>
    <!-- Свернутое состояние (Скриншот 13: Card_чек-лист свернут) -->
    <div
      v-if="isCollapsed"
      class="d-flex align-center justify-space-between cursor-pointer select-none py-2 px-3 border rounded-lg bg-surface mb-3"
      :class="{ 'checklist-error-border': error }"
      @click="isCollapsed = false"
    >
      <div class="d-flex align-center ga-2">
        <span class="text-body-1 font-weight-medium text-high-emphasis">
          {{ checklistTitle }}
        </span>
        <span class="text-body-1 font-weight-medium text-primary">
          {{ completedCount }}/{{ totalCount }}
        </span>
      </div>

      <VBtn variant="text" icon size="x-small" color="secondary" @click.stop="isCollapsed = false">
        <VIcon icon="ri-arrow-down-s-line" size="20" />
      </VBtn>
    </div>

    <!-- Развернутое состояние (Скриншот 12: Card_чек-лист создан) -->
    <div
      v-else
      class="bg-surface rounded-lg border pa-4 mb-3"
      :class="{ 'checklist-error-border': error }"
    >
      <!-- Шапка чек-листа: заголовок слева, иконки корзины и стрелки вверх справа -->
      <div class="d-flex align-center justify-space-between mb-3">
        <span class="text-h6 text-primary">
          {{ checklistTitle }}
        </span>

        <div class="d-flex align-center ga-1">
          <!-- Удаление всего чек-листа -->
          <VTooltip v-if="canEdit" location="top" text="Удалить чек-лист">
            <template #activator="{ props: tipProps }">
              <VBtn
                v-bind="tipProps"
                variant="text"
                icon
                size="x-small"
                color="secondary"
                @click="confirmDeleteChecklist"
              >
                <VIcon icon="ri-delete-bin-line" size="18" />
              </VBtn>
            </template>
          </VTooltip>

          <!-- Свернуть чек-лист -->
          <VTooltip location="top" text="Свернуть чек-лист">
            <template #activator="{ props: tipProps }">
              <VBtn
                v-bind="tipProps"
                variant="text"
                icon
                size="x-small"
                color="secondary"
                @click="isCollapsed = true"
              >
                <VIcon icon="ri-arrow-up-s-line" size="20" />
              </VBtn>
            </template>
          </VTooltip>
        </div>
      </div>

      <!-- Список пунктов -->
      <div class="checklist-items-list d-flex flex-column ga-1">
        <div
          v-for="item in currentItems"
          :key="item.id"
          class="checklist-item d-flex align-center justify-space-between py-1 px-1 rounded transition-all"
          :class="{
            'checklist-item--completed': item.is_completed,
            'checklist-item--editing': editingItemId === item.id,
          }"
        >
          <!-- Режим редактирования строки -->
          <div v-if="editingItemId === item.id" class="d-flex align-center ga-2 flex-grow-1">
            <VTextField
              v-model="editingText"
              density="compact"
              variant="outlined"
              hide-details
              autofocus
              class="flex-grow-1"
              @keydown.enter="saveItemEdit(item)"
              @keydown.esc="cancelItemEdit"
              @blur="saveItemEdit(item)"
            />
          </div>

          <!-- Обычный режим просмотра строки -->
          <template v-else>
            <div
              class="d-flex align-center ga-2 flex-grow-1 min-width-0 mr-2"
              :class="{ 'cursor-pointer': canToggle || canEdit }"
              @click="handleItemClick(item)"
            >
              <VCheckboxBtn
                inline
                :model-value="item.is_completed"
                density="compact"
                color="primary"
                class="flex-grow-0 flex-shrink-0"
                :readonly="!canToggle || !entryId"
              />
              <span class="text-body-1 text-high-emphasis checklist-item-text">
                {{ item.text }}
              </span>
            </div>

            <!-- Иконки действий (карандаш и корзина) -->
            <div v-if="canEdit" class="checklist-item-actions d-flex align-center ga-1 flex-shrink-0">
              <VTooltip location="top" text="Редактировать пункт">
                <template #activator="{ props: tipProps }">
                  <VBtn
                    v-bind="tipProps"
                    variant="text"
                    icon
                    size="x-small"
                    color="secondary"
                    @click.stop="startEditItem(item)"
                  >
                    <VIcon icon="ri-edit-line" size="15" />
                  </VBtn>
                </template>
              </VTooltip>

              <VTooltip location="top" text="Удалить пункт">
                <template #activator="{ props: tipProps }">
                  <VBtn
                    v-bind="tipProps"
                    variant="text"
                    icon
                    size="x-small"
                    color="secondary"
                    @click.stop="confirmDeleteItem(item)"
                  >
                    <VIcon icon="ri-delete-bin-line" size="15" />
                  </VBtn>
                </template>
              </VTooltip>
            </div>
          </template>
        </div>

        <!-- Инпут добавления нового пункта с чекбоксом слева -->
        <div v-if="isAddingItem" class="d-flex align-center ga-2 py-1 px-1">
          <VCheckboxBtn
            inline
            :model-value="false"
            density="compact"
            disabled
            class="flex-grow-0 flex-shrink-0"
          />
          <VTextField
            ref="newItemInputRef"
            v-model="newItemText"
            density="compact"
            variant="plain"
            placeholder="Текст пункта..."
            hide-details
            class="checklist-item-input flex-grow-1 text-body-1"
            @keydown.enter.prevent="submitNewItem"
            @keydown.esc="cancelNewItem"
            @blur="submitNewItem"
          />
        </div>

        <!-- Квадратная кнопка с плюсиком по скриншоту 12 -->
        <div v-if="canEdit && !isAddingItem" class="pt-1">
          <VTooltip location="top" text="Добавить пункт">
            <template #activator="{ props: tipProps }">
              <VBtn
                v-bind="tipProps"
                variant="tonal"
                color="primary"
                icon
                size="small"
                rounded="md"
                class="rounded-md"
                @click="startAddingItem"
              >
                <VIcon icon="ri-add-line" size="18" />
              </VBtn>
            </template>
          </VTooltip>
        </div>
      </div>

      <!-- Кнопка Сохранить в правом нижнем углу без дивайдера -->
      <div
        v-if="isAddingItem || editingItemId !== null"
        class="d-flex align-center justify-end pt-2"
      >
        <VBtn
          variant="text"
          size="small"
          color="primary"
          class="text-none font-weight-medium px-2"
          @mousedown.prevent="handleSaveCurrent"
          @click="handleSaveCurrent"
        >
          Сохранить
          <VIcon icon="ri-check-line" size="18" class="ms-1" />
        </VBtn>
      </div>
    </div>

    <!-- Диалог подтверждения удаления пункта -->
    <ConfirmDialog
      :is-dialog-visible="isDeleteItemDialogOpen"
      confirmation-question="Удалить пункт чек-листа?"
      confirm-title="Удалить пункт"
      confirm-btn="Удалить"
      reject-btn="Отмена"
      @confirm="handleDeleteItemConfirm"
      @update:is-dialog-visible="isDeleteItemDialogOpen = $event"
    />

    <!-- Диалог подтверждения удаления всего чек-листа (строго по Скриншоту 12) -->
    <ConfirmDialog
      :is-dialog-visible="isDeleteChecklistDialogOpen"
      confirmation-question="Данные не сохранятся. Вы действительно хотите удалить чек-лист?"
      confirm-title="Удаление чек-листа"
      confirm-btn="Удалить"
      reject-btn="Отменить"
      @confirm="handleDeleteChecklistConfirm"
      @update:is-dialog-visible="isDeleteChecklistDialogOpen = $event"
    />
  </div>
</template>

<script setup lang="ts">
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue';
import { useTasksStore } from '@/modules/tasks/stores/tasksStore';
import type { ITaskChecklist, ITaskChecklistItem } from '@/modules/tasks/types/ITaskChecklist';
import { computed, nextTick, onMounted, ref } from 'vue';

interface Props {
  entryId?: number
  checklist: ITaskChecklist
  canEdit?: boolean
  canToggle?: boolean
  initialCollapsed?: boolean
  error?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  canEdit: true,
  canToggle: true,
  initialCollapsed: false,
  error: false,
})

const emit = defineEmits<{
  (e: 'deleted', checklistId: number): void
}>()

const tasksStore = useTasksStore()

const isCollapsed = ref(props.initialCollapsed)
const isAddingItem = ref((props.checklist.items || []).length === 0)
const newItemInputRef = ref<any>(null)
const newItemText = ref('')

const editingItemId = ref<number | null>(null)
const editingText = ref('')

// Диалоги
const isDeleteItemDialogOpen = ref(false)
const itemToDelete = ref<ITaskChecklistItem | null>(null)

const isDeleteChecklistDialogOpen = ref(false)

const checklistTitle = computed(() => {
  return props.checklist.title || 'Чек-лист'
})

const currentItems = computed(() => {
  return props.checklist.items || []
})

const totalCount = computed(() => {
  return currentItems.value.length
})

const completedCount = computed(() => {
  return currentItems.value.filter(i => i.is_completed).length
})

function handleItemClick(item: ITaskChecklistItem) {
  if (!props.entryId || !props.canToggle) {
    if (props.canEdit) {
      startEditItem(item)
      return
    }
  }
  toggleItem(item)
}

async function toggleItem(item: ITaskChecklistItem) {
  if (!props.canToggle) return
  const originalState = item.is_completed
  // Оптимистичное обновление
  item.is_completed = !originalState
  if (props.entryId) {
    const updated = await tasksStore.toggleChecklistItem(props.entryId, props.checklist.id, item.id)
    if (!updated) {
      // Откат при ошибке
      item.is_completed = originalState
    }
  }
}

function startEditItem(item: ITaskChecklistItem) {
  editingItemId.value = item.id
  editingText.value = item.text
  nextTick(() => {
    const input = document.querySelector('.checklist-item--editing input') as HTMLInputElement | null
    if (input) {
      input.focus()
      const len = input.value.length
      input.setSelectionRange(len, len)
    }
  })
}

function cancelItemEdit() {
  editingItemId.value = null
  editingText.value = ''
}

async function saveItemEdit(item: ITaskChecklistItem) {
  if (editingItemId.value !== item.id) return
  const newText = editingText.value.trim()
  if (!newText || newText === item.text) {
    cancelItemEdit()
    return
  }

  const originalText = item.text
  item.text = newText
  editingItemId.value = null

  if (props.entryId) {
    const updated = await tasksStore.updateChecklistItem(props.entryId, props.checklist.id, item.id, newText)
    if (!updated) {
      item.text = originalText
    }
  }
}

function confirmDeleteItem(item: ITaskChecklistItem) {
  itemToDelete.value = item
  isDeleteItemDialogOpen.value = true
}

async function handleDeleteItemConfirm(confirmed: boolean) {
  if (confirmed && itemToDelete.value) {
    const itemId = itemToDelete.value.id
    if (props.entryId) {
      const success = await tasksStore.deleteChecklistItem(props.entryId, props.checklist.id, itemId)
      if (success) {
        const idx = props.checklist.items.findIndex(i => i.id === itemId)
        if (idx >= 0) {
          props.checklist.items.splice(idx, 1)
        }
      }
    } else {
      const idx = props.checklist.items.findIndex(i => i.id === itemId)
      if (idx >= 0) {
        props.checklist.items.splice(idx, 1)
      }
    }
  }
  isDeleteItemDialogOpen.value = false
  itemToDelete.value = null
}

function confirmDeleteChecklist() {
  isDeleteChecklistDialogOpen.value = true
}

async function handleDeleteChecklistConfirm(confirmed: boolean) {
  if (confirmed) {
    if (props.entryId) {
      const success = await tasksStore.deleteChecklist(props.entryId, props.checklist.id)
      if (success) {
        emit('deleted', props.checklist.id)
      }
    } else {
      emit('deleted', props.checklist.id)
    }
  }
  isDeleteChecklistDialogOpen.value = false
}

function focusNewItemInput() {
  nextTick(() => {
    const el = (newItemInputRef.value?.$el || newItemInputRef.value) as HTMLElement | null
    const input = el?.querySelector('input')
    if (input) {
      input.focus()
    }
  })
}

onMounted(() => {
  if (isAddingItem.value) {
    focusNewItemInput()
  }
})

function startAddingItem() {
  isAddingItem.value = true
  focusNewItemInput()
}

function cancelNewItem() {
  if (currentItems.value.length > 0) {
    isAddingItem.value = false
    newItemText.value = ''
  }
}

let isSubmittingItem = false

async function submitNewItem() {
  if (isSubmittingItem) return
  const text = newItemText.value.trim()
  if (!text) {
    isAddingItem.value = false
    newItemText.value = ''
    return
  }

  isSubmittingItem = true
  newItemText.value = ''
  isAddingItem.value = false

  try {
    if (props.entryId) {
      const created = await tasksStore.createChecklistItem(props.entryId, props.checklist.id, text)
      if (created) {
        props.checklist.items.push(created)
      }
    } else {
      props.checklist.items.push({
        id: Date.now() + Math.floor(Math.random() * 1000),
        checklist: props.checklist.id,
        text,
        is_completed: false,
      })
    }
  } finally {
    isSubmittingItem = false
  }
}

function handleSaveCurrent() {
  if (editingItemId.value !== null) {
    const item = currentItems.value.find(i => i.id === editingItemId.value)
    if (item) {
      saveItemEdit(item)
    }
  } else if (isAddingItem.value) {
    submitNewItem()
  }
}
</script>

<style scoped>
:deep(.v-selection-control) {
  min-height: auto;
}

.checklist-item-input :deep(.v-field) {
  --v-field-padding-top: 0px;
  --v-field-padding-bottom: 0px;
  min-height: auto;
}

.checklist-item-input :deep(.v-field__input) {
  padding-top: 0;
  padding-bottom: 0;
  min-height: auto;
}

.checklist-item:hover {
  background-color: rgba(var(--v-theme-on-surface), 0.03);
}

.checklist-item-text {
  word-break: break-word;
  overflow-wrap: anywhere;
  white-space: normal;
}

.checklist-error-border {
  border-color: rgb(var(--v-theme-error)) !important;
}
</style>
